import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import PasswordInput from '../components/ui/PasswordInput';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');
  const [email, setEmail] = useState(emailParam || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (emailParam) setEmail(emailParam);
  }, [emailParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (!token || !email) {
      toast.error('Lien invalide. Demandez un nouveau lien de réinitialisation.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/reset-password', {
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      toast.success('Mot de passe réinitialisé ! Vous pouvez vous connecter.');
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.email?.[0]
        || 'Ce lien est invalide ou a expiré. Demandez-en un nouveau.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <PublicHeader />
        <main id="main-content" className="flex-1 flex items-center justify-center pt-16 p-4" tabIndex={-1}>
          <div className="w-full max-w-md">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h1 className="text-2xl font-bold text-slate-800 mb-4">
                Lien invalide
              </h1>
              <p className="text-slate-600 mb-6">
                Ce lien de réinitialisation est invalide ou a expiré. Demandez-en un nouveau.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block px-6 py-2 min-h-[44px] bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Nouveau lien
              </Link>
            </div>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <PublicHeader />
        <main id="main-content" className="flex-1 flex items-center justify-center pt-16 p-4" tabIndex={-1}>
          <div className="w-full max-w-md">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <p className="text-success-600 font-medium mb-4">
                Mot de passe réinitialisé avec succès !
              </p>
              <p className="text-slate-600 text-sm mb-6">
                Redirection vers la connexion...
              </p>
              <Link
                to="/login"
                className="text-primary-600 hover:underline font-medium"
              >
                Se connecter maintenant
              </Link>
            </div>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicHeader />
      <main id="main-content" className="flex-1 flex items-center justify-center pt-16 p-4" tabIndex={-1}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-2xl font-bold text-slate-800 mb-2 text-center">
              Nouveau mot de passe
            </h1>
            <p className="text-slate-500 text-sm text-center mb-6">
              Choisissez un nouveau mot de passe sécurisé.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4" aria-label="Définir un nouveau mot de passe">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[44px]"
                  placeholder="votre@email.com"
                />
              </div>
              <PasswordInput
                id="reset-password"
                label="Nouveau mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Min. 8 caractères"
              />
              <PasswordInput
                id="reset-password-confirm"
                label="Confirmer le mot de passe"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Répétez le mot de passe"
              />
              <button
                type="submit"
                disabled={loading}
                aria-busy={loading}
                className="w-full py-2 px-4 min-h-[44px] bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                {loading ? 'Enregistrement...' : 'Réinitialiser le mot de passe'}
              </button>
            </form>
            <p className="mt-6 text-sm text-center text-slate-500">
              <Link to="/login" className="text-primary-600 hover:underline">
                Retour à la connexion
              </Link>
            </p>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
